import { useCallback, useEffect, useRef } from 'react';
import {
  PaymentsTerminalController,
  PaymentsTerminalError,
  StoredReader,
  TerminalActions,
  TerminalReader,
  TerminalStrategyMethods,
  TerminalStrategyProps,
} from '@frontend/payments-terminal-controller';
import { PickPartial, Prettify } from '@frontend/types';
import { useTerminalStatusStore } from '../stores/terminal-status-store';

export enum ReaderConnectErrors {
  alreadyInUse = 'Reader is currently in use.',
}

type PaymentIntentInfo = { clientSecret: string; id: string };

type UseTerminalPaymentSessionProps = Prettify<
  PickPartial<
    Omit<TerminalStrategyProps, 'onError' | 'onPaymentStatusChange' | 'enableServerDriven' | 'enableServerDrivenV2'>,
    'reader' | 'sdkFailIfReaderInUse' | 'locationId'
  >
> & {
  locationId: string | undefined;
  onPaymentSuccess?: () => void;
  invoiceId: string | undefined;
  paymentIntent: PaymentIntentInfo | undefined;
  paymentId: string | undefined;
  goBack: () => void;
  retryCreatePaymentIntent: () => Promise<{
    paymentIntent: PaymentIntentInfo;
    paymentId: string;
  }>;
  discoverReaders: () => Promise<TerminalReader[]>;
};

type CollectAndProcessPaymentArgs = Parameters<Awaited<TerminalStrategyMethods>['collectAndProcessPayment']>[0] & {
  actions?: TerminalActions;
  isRetryAction?: boolean;
};

type RetryPaymentOptions = {
  paymentIntent: CollectAndProcessPaymentArgs['paymentIntent'];
  paymentId: CollectAndProcessPaymentArgs['paymentId'];
  invoiceId: CollectAndProcessPaymentArgs['invoiceId'];
  failIfReaderInUse: boolean;
  selectedReader: StoredReader;
};

type OptionalProps<T> = Partial<Record<keyof T, undefined>>;
type TightProps<T> = T | OptionalProps<T>;
type ReaderPaymentRetryHandlerOptions = TightProps<{
  selectedReader: StoredReader | undefined;
  failIfReaderInUse: boolean;
}>;

export type ReaderPaymentRetryHandler = (options?: ReaderPaymentRetryHandlerOptions) => void;

/**
 * This hook is used to manage the terminal payment session of both the server-driven and sdk strategies.
 *
 * See [Terminal Payments Flowchart]{@link https://drive.google.com/file/d/1ZHd1HElTDGwqnVPENfrwkXSXqouKHArO/view?usp=sharing}
 */
export const useTerminalPaymentSession = ({
  reader,
  locationId,
  appData,
  paymentIntent,
  paymentId,
  invoiceId,
  goBack,
  discoverReaders,
  onPaymentSuccess,
  retryCreatePaymentIntent,
}: UseTerminalPaymentSessionProps) => {
  const terminalStatusStore = useTerminalStatusStore({ locationId });

  const activeReader = terminalStatusStore.getActiveReader();

  /**
   * Payment status is processing or success
   */
  const isTerminalStateDisabled = terminalStatusStore.activeReaderMethods.isInRestrictedState();

  /**
   * Props.reader is not the same as active reader
   */
  const isInitializing = terminalStatusStore.isInitializingReader();

  /**
   * Payment was successful
   */
  const isPaymentSuccessful = activeReader?.status === 'success';

  /**
   * Has is initializing collection
   */
  const isStartingCollection = terminalStatusStore.activeReaderMethods.isStartingCollection();

  const mounted = useRef(true);
  const storeOnce = useRef('');

  const cancelPayment = useCallback(async (overrideReaderId?: string) => {
    const readerId = overrideReaderId || terminalStatusStore.getActiveReader()?.reader.readerId;
    if (!readerId) {
      return false;
    }

    const res = await terminalStatusStore.cancelOngoingPayment(readerId);

    return res;
  }, []);

  const initializePaymentSession = useCallback(
    async (selectedReader = reader, sdkFailIfReaderInUse = true) => {
      if (!selectedReader || !locationId || !appData) {
        return;
      }

      if (terminalStatusStore.isReaderInRestrictedState(selectedReader.readerId)) {
        return;
      }

      if (!paymentIntent) {
        terminalStatusStore.setReaderStatus(selectedReader.readerId, {
          status: 'error',
          error: {
            action: 'initialize',
            message: 'Payment intent is required to initialize terminal session',
          },
        });
        return;
      }

      let latestPaymentIntent = paymentIntent;

      terminalStatusStore.setInitializingReaderId(selectedReader.readerId);

      try {
        await terminalStatusStore.cancelAllOngoingPayments();
      } catch (error) {
        console.warn('Error cancelling previous payment before initializing terminal session', { error });
      }

      let newActions: TerminalStrategyMethods;
      try {
        newActions = await PaymentsTerminalController.createTerminalSession({
          locationId,
          reader: selectedReader,
          appData,
          enableServerDriven: true,
          onPaymentStatusChange(status, staleReaderId) {
            if (!mounted.current) return;

            terminalStatusStore.setReaderStatus(staleReaderId, { status });

            if (status === 'success') {
              onPaymentSuccess?.();
            }
          },
          sdkFailIfReaderInUse,
        });
      } catch (error) {
        let initializationError = {
          action: 'initialize' as const,
          message: 'An unexpected error occurred',
        };
        if (error instanceof PaymentsTerminalError) {
          initializationError = { action: 'initialize', message: error.message };
        }
        terminalStatusStore.setReaderStatus(selectedReader.readerId, { status: 'error', error: initializationError });

        return;
      }

      if (!mounted.current) {
        /**
         * Component might have unmounted during setup
         */
        return;
      }

      const readers = await discoverReaders();
      const discoveredServerReader = readers.find((r) => r.id === selectedReader.readerId);

      if (newActions.usingServerDrivenFlow) {
        if (discoveredServerReader?.action?.status === 'in_progress') {
          await cancelPayment(selectedReader.readerId);
        }
        if (
          (terminalStatusStore.hasActiveReader() || terminalStatusStore.isPaymentIntentStale(paymentIntent.id)) &&
          terminalStatusStore.activeReaderMethods.isIntentCancelled(paymentIntent.id)
        ) {
          const newPaymentIntentData = await retryCreatePaymentIntent();
          latestPaymentIntent = newPaymentIntentData.paymentIntent;
        }
      }

      if (!mounted.current) {
        /**
         * Component might have unmounted during setup
         */
        return;
      }

      await terminalStatusStore.setActiveReader(selectedReader, {
        actions: { ...newActions, readerId: selectedReader.readerId },
        paymentIntentId: latestPaymentIntent.id,
      });
    },
    [reader, locationId, appData, paymentIntent]
  );

  const collectAndProcessPayment = useCallback(
    async ({
      paymentIntent: paymentIntentProp,
      paymentId: paymentIdProp,
      invoiceId: invoiceIdProp,
      actions,
      //failIfReaderInUse will be false only when overriding existing payments
      failIfReaderInUse = true,
      isRetryAction = false,
    }: CollectAndProcessPaymentArgs) => {
      // Check needs to be here to prevent duplicate collects from running
      if (terminalStatusStore.isCollectingPayment(actions?.readerId)) {
        return;
      }

      actions = actions ?? terminalStatusStore.getActiveReader()?.actions;

      const targetReader = terminalStatusStore.getActiveReader();

      if ((!isRetryAction && targetReader?.error) || !actions || !actions.readerId) {
        terminalStatusStore.activeReaderMethods.setErrorState({
          action: 'collect',
          message: 'Terminal actions not initialized',
        });
        return;
      }

      if (!terminalStatusStore.isActiveReader(actions.readerId)) {
        terminalStatusStore.activeReaderMethods.setErrorState({
          action: 'collect',
          message: 'Cannot collect with inactive reader',
        });
        return;
      }

      terminalStatusStore.activeReaderMethods.setStartCollecting('starting');

      try {
        if (targetReader?.isCancelingPromise) {
          throw new PaymentsTerminalError('collect', 'Cannot collect payment while canceling previous action');
        }

        terminalStatusStore.activeReaderMethods.setStartCollecting('collecting');

        const res = await actions?.collectAndProcessPayment({
          paymentIntent: paymentIntentProp,
          paymentId: paymentIdProp,
          invoiceId: invoiceIdProp,
          failIfReaderInUse,
        });

        terminalStatusStore.activeReaderMethods.setStopCollecting('complete');

        return res;
      } catch (error) {
        if (!mounted.current) return;
        terminalStatusStore.activeReaderMethods.setStopCollecting(null);

        if (error instanceof PaymentsTerminalError) {
          if (error.code === 'canceled') return; // good idea?
          terminalStatusStore.activeReaderMethods.setErrorState({ action: error.action, message: error.message });
        } else if (error instanceof Error) {
          terminalStatusStore.activeReaderMethods.setErrorState({ action: 'collect', message: error.message });
        } else {
          terminalStatusStore.activeReaderMethods.setErrorState({
            action: 'collect',
            message: 'An unexpected error occurred',
          });
        }
      }

      return;
    },
    []
  );

  const retryPayment = useCallback(
    async ({
      paymentIntent: paymentIntentProp,
      paymentId: paymentIdProp,
      invoiceId: invoiceIdProp,
      failIfReaderInUse,
      selectedReader,
    }: RetryPaymentOptions) => {
      const targetReader = terminalStatusStore.getReaderState(selectedReader.readerId);
      if (!targetReader) {
        console.warn('retryPayment(): No active reader to retry payment for.');
        return;
      }

      terminalStatusStore.activeReaderMethods.setForRetryPayment();

      const isActiveReader = terminalStatusStore.isActiveReader(targetReader.reader.readerId);

      if (!isActiveReader) {
        await initializePaymentSession(selectedReader, failIfReaderInUse);
      }
      await collectAndProcessPayment({
        paymentIntent: paymentIntentProp,
        paymentId: paymentIdProp,
        invoiceId: invoiceIdProp,
        actions: targetReader.actions,
        isRetryAction: true,
        failIfReaderInUse,
      });
    },
    [initializePaymentSession, collectAndProcessPayment]
  );

  const handleCancel = useCallback(async () => {
    try {
      await cancelPayment();
    } catch (error) {
      console.warn('Error cancelling payment', { error });
    } finally {
      goBack();
    }
  }, [goBack]);

  const handleTryAgain: ReaderPaymentRetryHandler = useCallback(
    async ({ selectedReader = reader, failIfReaderInUse = true } = {}) => {
      if (!selectedReader || !paymentIntent || !paymentId) return;
      try {
        await retryPayment({
          paymentIntent,
          paymentId,
          invoiceId,
          failIfReaderInUse,
          selectedReader,
        });
      } catch (error) {
        console.warn('Error retrying payment', { error });
      }
    },
    [retryPayment, reader, paymentIntent, paymentId, invoiceId]
  );

  // Cleanup on unmount
  useEffect(() => {
    mounted.current = true;

    const cancelPaymentOnUnmount = () => {
      cancelPayment(terminalStatusStore.getActiveReader()?.reader.readerId).catch((error) => {
        if (!mounted.current) return;
        if (error instanceof PaymentsTerminalError) {
          terminalStatusStore.activeReaderMethods.setErrorState({ action: error.action, message: error.message });
        } else {
          terminalStatusStore.activeReaderMethods.setErrorState({
            action: 'cancel',
            message: 'An unexpected error occurred',
          });
        }
      });
    };

    window.addEventListener('beforeunload', cancelPaymentOnUnmount);

    return () => {
      /**
       * Prevent ongoing actions from happening for unmounted component
       */
      mounted.current = false;
      window.removeEventListener('beforeunload', cancelPaymentOnUnmount);

      /**
       * Clear active reader and ongoing payments
       */
      terminalStatusStore.clearAllReaders();

      /**
       * Clear initializing reader
       */
      terminalStatusStore.setInitializingReaderId(null);
    };
  }, []);

  // Store reader when successful
  useEffect(() => {
    const hasRequiredValues = reader && locationId && appData;
    if (!hasRequiredValues || activeReader?.status !== 'success' || storeOnce.current === reader.readerId) {
      return;
    }
    PaymentsTerminalController.storeReader(locationId, reader);
    storeOnce.current = reader.readerId;
  }, [activeReader?.status, locationId, reader, appData]);

  // INITIATE COLLECTION
  useEffect(() => {
    const terminalActions = activeReader?.actions;
    const hasRequiredValues = !!paymentIntent && !!paymentId && !!invoiceId;

    if (isInitializing) {
      return;
    }

    if (activeReader?.error || !hasRequiredValues) {
      return;
    }

    const isReaderReady = !!reader && !!terminalActions?.readerId;
    const hasChangedReader =
      reader?.readerId && !!activeReader?.reader.readerId && activeReader?.reader.readerId !== reader?.readerId;

    const readerHadItsCollectionStarted = terminalStatusStore.activeReaderMethods.hasStartedCollectingPayment();

    if (hasChangedReader || !isReaderReady) {
      /**
       * Step 1: Initialize terminal session when:
       */
      initializePaymentSession();
    } else if (!readerHadItsCollectionStarted) {
      /**
       * Step 2: Collect and process payment when:
       * useEffect() will run again when initialized
       */
      collectAndProcessPayment({
        paymentIntent,
        paymentId,
        invoiceId,
      });
    }
  }, [reader?.readerId, activeReader?.reader.readerId, paymentIntent, paymentId, invoiceId, isInitializing]);

  return {
    error: activeReader?.error ?? undefined,
    status: activeReader?.status ?? 'not_ready',
    terminalActions: activeReader?.actions,
    isTerminalStateDisabled,
    isLoading: isInitializing || isStartingCollection,
    handleCancel,
    cancelPayment,
    handleTryAgain,
    isPaymentSuccessful,
  };
};
